import { authorizedAPI } from '@/config/axios.config';
import type {
    ProvisionDeploymentDto,
    ProvisioningJob,
    ProvisioningLogPage,
    SshCredentialSummary,
} from './types';

/**
 * Every method here calls `authorizedAPI` directly rather than going through
 * `UtilsService.handleApiRequest`.
 *
 * That helper returns the *error body as data* for any non-401 response. For
 * `getLogs` that would mean a failed poll resolving with `{ message: '...' }`,
 * which the log hook would then merge into its event map and the console would
 * crash trying to render. For `provision` it would mean a 409 "already in
 * flight" silently resolving as success. `deploymentService.deploy()` already
 * bypasses it for exactly this reason.
 */
class ProvisioningService {
    async provision(
        deploymentId: string,
        data: ProvisionDeploymentDto,
    ): Promise<ProvisioningJob> {
        const res = await authorizedAPI.post<ProvisioningJob>(
            `/deployments/${deploymentId}/provision`,
            data,
        );
        return res.data;
    }

    async getJobs(deploymentId: string): Promise<ProvisioningJob[]> {
        const res = await authorizedAPI.get<ProvisioningJob[]>(
            `/deployments/${deploymentId}/provisioning/jobs`,
        );
        return res.data;
    }

    async getJob(jobId: string): Promise<ProvisioningJob> {
        const res = await authorizedAPI.get<ProvisioningJob>(
            `/provisioning/jobs/${jobId}`,
        );
        return res.data;
    }

    async getLogs(
        jobId: string,
        after = 0,
        limit = 500,
    ): Promise<ProvisioningLogPage> {
        const res = await authorizedAPI.get<ProvisioningLogPage>(
            `/provisioning/jobs/${jobId}/logs`,
            { params: { after, limit } },
        );
        return res.data;
    }

    async cancel(jobId: string): Promise<ProvisioningJob> {
        const res = await authorizedAPI.post<ProvisioningJob>(
            `/provisioning/jobs/${jobId}/cancel`,
        );
        return res.data;
    }

    async getSshCredential(
        deploymentId: string,
    ): Promise<SshCredentialSummary | null> {
        const res = await authorizedAPI.get<SshCredentialSummary | null>(
            `/deployments/${deploymentId}/ssh-credential`,
        );
        return res.data;
    }

    async setSshCredential(
        deploymentId: string,
        sshCredential: { type: 'PASSWORD' | 'PRIVATE_KEY'; secret: string },
    ): Promise<SshCredentialSummary> {
        const res = await authorizedAPI.put<SshCredentialSummary>(
            `/deployments/${deploymentId}/ssh-credential`,
            { sshCredential },
        );
        return res.data;
    }

    async clearSshCredential(deploymentId: string): Promise<{ message: string }> {
        const res = await authorizedAPI.delete<{ message: string }>(
            `/deployments/${deploymentId}/ssh-credential`,
        );
        return res.data;
    }
}

export const provisioningService = new ProvisioningService();
